import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import type {
  Popup,
  PopupsResponse,
  CreatePopupRequest,
  UpdatePopupRequest,
  PopupFilters,
  PopupCountFilters,
  PopupCountByStatus,
  PopupSearchFilter,
} from '../models/Popup';
import type { PopupEventsFilters, PopupEventsResponse } from '../models/PopupEvent';

export interface MyPopupsParams {
  limit?: number;
  sorting?: {
    column?: 'expires_at' | 'created_at' | 'title';
    direction?: 'asc' | 'desc';
  };
}

/**
 * Construye los parámetros advanced_filters para la API
 * Formato: advanced_filters[filters][0][column]=status, etc.
 */
function buildAdvancedFiltersParams(searchFilter: PopupSearchFilter): Record<string, any> {
  const params: Record<string, any> = {};

  searchFilter.filters.forEach((filter, index) => {
    params[`advanced_filters[filters][${index}][column]`] = filter.column;
    params[`advanced_filters[filters][${index}][operator]`] = filter.operator;

    if (filter.operator === 'in' || filter.operator === 'not_in') {
      if (Array.isArray(filter.value)) {
        filter.value.forEach((val, valIndex) => {
          params[`advanced_filters[filters][${index}][value][${valIndex}]`] = val;
        });
      } else if (filter.value) {
        params[`advanced_filters[filters][${index}][value][0]`] = filter.value;
      }
    } else if (filter.operator !== 'is_null' && filter.operator !== 'is_not_null') {
      if (filter.value !== undefined && filter.value !== null) {
        params[`advanced_filters[filters][${index}][value]`] = filter.value;
      }
    }
  });

  params['advanced_filters[logic]'] = searchFilter.logic;

  return params;
}

function buildPopupQueryParams(
  filters?: PopupFilters | PopupCountFilters,
  options?: { includePagination?: boolean; includeSorting?: boolean },
): Record<string, unknown> {
  const params: Record<string, unknown> = {};
  const listFilters = filters as PopupFilters | undefined;

  if (filters?.search) {
    params.search = filters.search;
  }

  if (filters?.searchFilter) {
    Object.assign(params, buildAdvancedFiltersParams(filters.searchFilter));
  }

  if (options?.includePagination) {
    if (listFilters?.page) params.page = listFilters.page.toString();
    if (listFilters?.perPage) params.per_page = listFilters.perPage.toString();
  }

  if (options?.includeSorting && listFilters?.sorting?.column) {
    params['sorting[column]'] = listFilters.sorting.column;
    if (listFilters.sorting.direction) {
      params['sorting[direction]'] = listFilters.sorting.direction;
    }
  }

  return params;
}

export class PopupApiAdapter {
  private readonly basePath = '/popups';
  private readonly myBasePath = '/my/popups';
  private readonly httpClient = createHttpClient(apiClientWH);

  async getMyPopups(params?: MyPopupsParams): Promise<Popup[]> {
    const queryParams: Record<string, string> = {};

    if (params?.limit) {
      queryParams.limit = params.limit.toString();
    }
    if (params?.sorting?.column) {
      queryParams['sorting[column]'] = params.sorting.column;
    }
    if (params?.sorting?.direction) {
      queryParams['sorting[direction]'] = params.sorting.direction;
    }

    const response = await this.httpClient.get<Popup[]>(this.myBasePath, queryParams);
    return response;
  }

  async markPopupAsRead(teamPopupId: number): Promise<void> {
    await this.httpClient.post(`${this.myBasePath}/${teamPopupId}/read`, {});
  }

  async getPopups(filters?: PopupFilters): Promise<PopupsResponse> {
    const params = buildPopupQueryParams(filters, { includePagination: true, includeSorting: true });
    const response = await this.httpClient.get<PopupsResponse>(this.basePath, params);
    return response;
  }

  async getCountByStatus(filters?: PopupCountFilters): Promise<PopupCountByStatus> {
    const params = buildPopupQueryParams(filters);
    const response = await this.httpClient.get<PopupCountByStatus>(`${this.basePath}/count-by-status`, params);
    return response;
  }

  async getPopup(id: number): Promise<Popup> {
    const response = await this.httpClient.get<Popup>(`${this.basePath}/${id}`);
    return response;
  }

  async createPopup(data: CreatePopupRequest): Promise<Popup> {
    const response = await this.httpClient.post<Popup>(this.basePath, data);
    return response;
  }

  async updatePopup(id: number, data: UpdatePopupRequest): Promise<Popup> {
    const response = await this.httpClient.put<Popup>(`${this.basePath}/${id}`, data);
    return response;
  }

  async deletePopup(id: number): Promise<void> {
    await this.httpClient.delete(`${this.basePath}/${id}`);
  }

  async archivePopup(id: number): Promise<Popup> {
    const response = await this.httpClient.put<Popup>(`${this.basePath}/${id}`, {
      status: 'archived',
    });
    return response;
  }

  async getPopupEvents(popupId: number, filters?: PopupEventsFilters): Promise<PopupEventsResponse> {
    const params: Record<string, string> = {};

    if (filters?.type) {
      params.type = filters.type;
    }
    if (filters?.team_id) {
      params.team_id = filters.team_id.toString();
    }
    if (filters?.page) {
      params.page = filters.page.toString();
    }
    if (filters?.perPage) {
      params.per_page = filters.perPage.toString();
    }
    if (filters?.sorting?.column) {
      params['sorting[column]'] = filters.sorting.column;
    }
    if (filters?.sorting?.direction) {
      params['sorting[direction]'] = filters.sorting.direction;
    }

    const response = await this.httpClient.get<PopupEventsResponse>(`${this.basePath}/${popupId}/events`, params);
    return response;
  }
}

export const popupApiAdapter = new PopupApiAdapter();
